/**
 * Lightweight particle field for explosion bursts on bike death.
 * Runs on its own real-time clock (not the fixed sim step) so it stays smooth.
 */

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
}

const DRAG = 0.92;

export class ParticleField {
  private particles: Particle[] = [];

  /** Radial burst of `count` shards from a point. */
  burst(x: number, y: number, color: string, count = 26): void {
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.4;
      const speed = 60 + Math.random() * 220;
      const maxLife = 0.4 + Math.random() * 0.5;
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: maxLife,
        maxLife,
        size: 1 + Math.random() * 2.5,
        color,
      });
    }
  }

  get active(): boolean {
    return this.particles.length > 0;
  }

  /** `dt` in seconds. */
  update(dt: number): void {
    for (const p of this.particles) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= DRAG;
      p.vy *= DRAG;
      p.life -= dt;
    }
    this.particles = this.particles.filter((p) => p.life > 0);
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.particles.length === 0) return;
    ctx.save();
    ctx.shadowBlur = 8;
    for (const p of this.particles) {
      ctx.globalAlpha = Math.max(0, p.life / p.maxLife);
      ctx.shadowColor = p.color;
      ctx.fillStyle = p.color;
      ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
    }
    ctx.restore();
  }

  clear(): void {
    this.particles.length = 0;
  }
}
